import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChildLockSummary, GatewayInfoResponse, LockResponse } from "api/ApiClient";
import axios from "axios";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BASE_API_URL } from "store/Me";
import { parseLastSeenTimestamp } from "utils/parseLastSeenTimestamp";
import { Alert } from "components/ui/alert";
import { Button } from "components/ui/button";
import { AdminTable } from "components/shared/AdminTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import DeviceTypeFirmware from "./DeviceFirmware";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type TabValue = "devices" | "firmware" | "time";

type GatewayTimeDriftResponse = {
  lockId: string;
  gatewaySystemLockId: string;
  siteTimeZone?: string;
  serverUtc: string;
  gatewayUtc?: string;
  serverSiteLocalFormatted?: string;
  gatewaySiteLocalFormatted?: string;
  driftSeconds?: number;
  driftState: "ok" | "warning" | "critical" | "unknown";
  dstSupported: boolean;
  dstActive?: boolean;
  lastSyncUtc?: string;
};

const DRIFT_STATE_CLASS_NAMES: Record<
  GatewayTimeDriftResponse["driftState"],
  string
> = {
  ok: "text-emerald-600",
  warning: "text-amber-600",
  critical: "text-red-600",
  unknown: "text-slate-500",
};

function GatewayDetailsTable({ children }: { children: React.ReactNode }) {
  return (
    <AdminTable
      aria-label="Gateway firmware details"
      className="[&_th]:w-2/5 [&_th]:font-medium [&_th]:align-top [&_th]:text-slate-500 [&_td]:text-slate-800"
    >
      <tbody>{children}</tbody>
    </AdminTable>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Root component
// ─────────────────────────────────────────────────────────────────────────────

export default function GatewayDetailView({ lock }: { lock: LockResponse }) {
  const [activeTab, setActiveTab] = useState<TabValue>("devices");
  const isOffline = lock.onlineStatus === "Offline";

  const { data: gatewayInfo } = useQuery({
    queryKey: ["locks", "gateway", lock.id],
    queryFn: () => fetchGatewayInfo(lock.id!),
    enabled: !isOffline,
  });

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabValue)}>
        <TabsList className="justify-start w-full overflow-x-auto overflow-y-hidden">
          <TabsTrigger
            className="shrink-0 data-[state=active]:bg-slate-200!"
            value="devices"
          >
            Connected Devices
          </TabsTrigger>
          <TabsTrigger
            className="shrink-0 data-[state=active]:bg-slate-200!"
            value="firmware"
          >
            Firmware
          </TabsTrigger>
          <TabsTrigger
            className="shrink-0 data-[state=active]:bg-slate-200!"
            value="time"
          >
            Time
          </TabsTrigger>
        </TabsList>

        <TabsContent className="pt-4" value="devices">
          <ConnectedDevicesTab
            lock={lock}
            gatewayInfo={gatewayInfo}
            isOffline={isOffline}
          />
        </TabsContent>
        <TabsContent className="pt-4" value="firmware">
          <FirmwareTab lock={lock} gatewayInfo={gatewayInfo} isOffline={isOffline} />
        </TabsContent>
        <TabsContent className="pt-4" value="time">
          <TimeTab
            lockId={lock.id!}
            isOffline={isOffline}
            siteTimeZone={lock.site?.timeZone}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Connected Devices tab
// ─────────────────────────────────────────────────────────────────────────────

function ConnectedDevicesTab({
  lock,
  gatewayInfo,
  isOffline,
}: {
  lock: LockResponse;
  gatewayInfo: GatewayInfoResponse | undefined;
  isOffline: boolean;
}) {
  const [showLive, setShowLive] = useState(false);

  const cachedDevices = (lock.cachedChildDevices ?? []) as ChildLockSummary[];
  const hasCached = cachedDevices && cachedDevices.length > 0;

  if (showLive) {
    return (
      <div className="space-y-3">
        <div>
          <Button
            onClick={() => setShowLive(false)}
            size="sm"
            type="button"
            variant="outline"
          >
            Back to Database
          </Button>
        </div>
        {gatewayInfo?.locks && gatewayInfo.locks.length > 0 ? (
          <AdminTable aria-label="Live gateway connected devices">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">S/N</th>
                <th scope="col">Type</th>
                <th scope="col">Link ID</th>
                <th scope="col">Comm</th>
                <th scope="col">Signal</th>
                <th scope="col">Battery</th>
              </tr>
            </thead>
            <tbody>
              {gatewayInfo.locks.map((lk) => (
                <tr key={lk.deviceId}>
                  <td>{lk.deviceName}</td>
                  <td className="font-mono text-xs">{lk.deviceId}</td>
                  <td>{lk.modelType}</td>
                  <td className="font-mono text-xs">{lk.linkId}</td>
                  <td>{lk.linkCommunicationStatus ?? "—"}</td>
                  <td>{lk.signalQuality ?? "—"}</td>
                  <td>{lk.batteryVoltage ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </AdminTable>
        ) : (
          <p className="text-sm text-slate-500">
            {isOffline
              ? "Gateway offline — cannot load live list."
              : "Loading live device list..."}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <Button
          disabled={isOffline}
          onClick={() => setShowLive(true)}
          size="sm"
          type="button"
        >
          Live
        </Button>
      </div>

      {hasCached ? (
        <>
          {lock.lastSeenTimeStamp ? (
            <p className="text-xs text-slate-500">
              Database view as of {parseLastSeenTimestamp(lock.lastSeenTimeStamp)?.toLocaleString() ?? "Unknown"}
            </p>
          ) : null}
          <AdminTable aria-label="Cached gateway connected devices">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">S/N</th>
                <th scope="col">Type</th>
                <th scope="col">Last Seen</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {cachedDevices!.map((dev: ChildLockSummary) => (
                <tr key={dev.id}>
                  <td>
                    <Link to={`/admin/locks/${dev.id}`}>{dev.name}</Link>
                  </td>
                  <td className="font-mono text-xs">{dev.systemLockId}</td>
                  <td>{dev.deviceType}</td>
                  <td>
                    {dev.lastSeenTimeStamp
                      ? parseLastSeenTimestamp(dev.lastSeenTimeStamp)?.toLocaleString() ?? "—"
                      : "—"}
                  </td>
                  <td>{dev.onlineStatus ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </AdminTable>
        </>
      ) : (
        <p className="text-sm text-slate-500">No connected devices in the database.</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Firmware tab
// ─────────────────────────────────────────────────────────────────────────────

type FirmwareSource = "cached" | "live";

function FirmwareTab({
  lock,
  gatewayInfo,
  isOffline,
}: {
  lock: LockResponse;
  gatewayInfo: GatewayInfoResponse | undefined;
  isOffline: boolean;
}) {
  const [infoSource, setInfoSource] = useState<FirmwareSource>("cached");

  const hasCachedFirmware =
    lock.firmwareMain ||
    lock.firmwareBLE2 ||
    lock.firmwareMacAdr ||
    lock.firmwareMainBl ||
    lock.firmwareEthernetMacAdr ||
    lock.firmwareLock ||
    lock.firmwareUpdateVersion ||
    lock.configSyncTimestamp ||
    lock.auditSyncTimestamp ||
    lock.databaseSyncTimestamp;

  return (
    <div className="space-y-4">
      <div>
        {infoSource === "live" ? (
          <Button
            onClick={() => setInfoSource("cached")}
            size="sm"
            type="button"
            variant="outline"
          >
            Back to Database
          </Button>
        ) : (
          <Button
            disabled={isOffline}
            onClick={() => setInfoSource("live")}
            size="sm"
            type="button"
          >
            Live
          </Button>
        )}
      </div>

      {infoSource === "cached" ? (
        hasCachedFirmware ? (
          <GatewayDetailsTable>
              {lock.firmwareMain ? (
                <tr>
                  <th>Main Firmware</th>
                  <td className="font-mono">{lock.firmwareMain}</td>
                </tr>
              ) : null}
              {lock.firmwareLock ? (
                <tr>
                  <th>Lock Firmware</th>
                  <td className="font-mono">{lock.firmwareLock}</td>
                </tr>
              ) : null}
              {lock.firmwareUpdateVersion ? (
                <tr>
                  <th>Queued Firmware</th>
                  <td className="font-mono text-amber-600">{lock.firmwareUpdateVersion}</td>
                </tr>
              ) : null}
              {lock.firmwareBLE2 ? (
                <tr>
                  <th>BLE Firmware</th>
                  <td className="font-mono">{lock.firmwareBLE2}</td>
                </tr>
              ) : null}
              {lock.firmwareMainBl ? (
                <tr>
                  <th>Bootloader</th>
                  <td className="font-mono">{lock.firmwareMainBl}</td>
                </tr>
              ) : null}
              {lock.firmwareMacAdr ? (
                <tr>
                  <th>MAC Address</th>
                  <td className="font-mono">{lock.firmwareMacAdr}</td>
                </tr>
              ) : null}
              {lock.firmwareEthernetMacAdr ? (
                <tr>
                  <th>Ethernet MAC</th>
                  <td className="font-mono">{lock.firmwareEthernetMacAdr}</td>
                </tr>
              ) : null}
              {lock.configSyncTimestamp ? (
                <tr>
                  <th>Config Synced</th>
                  <td>{parseLastSeenTimestamp(lock.configSyncTimestamp)?.toLocaleString() ?? "—"}</td>
                </tr>
              ) : null}
              {lock.auditSyncTimestamp ? (
                <tr>
                  <th>Audits Synced</th>
                  <td>{parseLastSeenTimestamp(lock.auditSyncTimestamp)?.toLocaleString() ?? "—"}</td>
                </tr>
              ) : null}
              {lock.databaseSyncTimestamp ? (
                <tr>
                  <th>DB Synced</th>
                  <td>{parseLastSeenTimestamp(lock.databaseSyncTimestamp)?.toLocaleString() ?? "—"}</td>
                </tr>
              ) : null}
          </GatewayDetailsTable>
        ) : (
          <p className="text-sm text-slate-500">No cached firmware data in the database yet.</p>
        )
      ) : gatewayInfo?.information ? (
        <GatewayDetailsTable>
            {gatewayInfo.information.firmwareVersion ? (
              <tr>
                <th>Main Firmware</th>
                <td className="font-mono">
                  {gatewayInfo.information.firmwareVersion}
                </td>
              </tr>
            ) : null}
            {gatewayInfo.information.modelType ? (
              <tr>
                <th>Model Type</th>
                <td>{gatewayInfo.information.modelType}</td>
              </tr>
            ) : null}
            {gatewayInfo.information.firmwareBootloaderVersion ? (
              <tr>
                <th>Bootloader</th>
                <td className="font-mono">
                  {gatewayInfo.information.firmwareBootloaderVersion}
                </td>
              </tr>
            ) : null}
            {gatewayInfo.information.firmwareBleVersion ? (
              <tr>
                <th>BLE Firmware</th>
                <td className="font-mono">
                  {gatewayInfo.information.firmwareBleVersion}
                </td>
              </tr>
            ) : null}
            {gatewayInfo.information.firmwareMacAddress ? (
              <tr>
                <th>MAC Address</th>
                <td className="font-mono">
                  {gatewayInfo.information.firmwareMacAddress}
                </td>
              </tr>
            ) : null}
            {gatewayInfo.information.firmwareEthernetMacAddress ? (
              <tr>
                <th>Ethernet MAC</th>
                <td className="font-mono">
                  {gatewayInfo.information.firmwareEthernetMacAddress}
                </td>
              </tr>
            ) : null}
            {gatewayInfo.information.operationalDetails?.deviceName ? (
              <tr>
                <th>Gateway Device Name</th>
                <td>{gatewayInfo.information.operationalDetails.deviceName}</td>
              </tr>
            ) : null}
            {gatewayInfo.information.operationalDetails?.mainSerialNumber ? (
              <tr>
                <th>Main Serial</th>
                <td className="font-mono">
                  {gatewayInfo.information.operationalDetails.mainSerialNumber}
                </td>
              </tr>
            ) : null}
            {gatewayInfo.information.operationalDetails?.hardwareVersion ? (
              <tr>
                <th>Hardware Version</th>
                <td>{gatewayInfo.information.operationalDetails.hardwareVersion}</td>
              </tr>
            ) : null}
            {gatewayInfo.information.operationalDetails?.daysInUse ? (
              <tr>
                <th>Days In Use</th>
                <td>{gatewayInfo.information.operationalDetails.daysInUse}</td>
              </tr>
            ) : null}
            {gatewayInfo.information.operationalDetails?.upTime ? (
              <tr>
                <th>Uptime</th>
                <td>{gatewayInfo.information.operationalDetails.upTime}</td>
              </tr>
            ) : null}
            {gatewayInfo.information.networkDetails?.ethernetAssignedIp ? (
              <tr>
                <th>Ethernet IP</th>
                <td className="font-mono">
                  {gatewayInfo.information.networkDetails.ethernetAssignedIp}
                </td>
              </tr>
            ) : null}
            {gatewayInfo.information.networkDetails?.wifiAssignedIp ? (
              <tr>
                <th>WiFi IP</th>
                <td className="font-mono">
                  {gatewayInfo.information.networkDetails.wifiAssignedIp}
                </td>
              </tr>
            ) : null}
        </GatewayDetailsTable>
      ) : (
        <p className="text-sm text-slate-500">
          {isOffline
            ? "Gateway offline — switch to Cached view."
            : "Loading live gateway info..."}
        </p>
      )}

      {/* Firmware upgrade controls — only available on live view with gateway data */}
      {infoSource === "live" && gatewayInfo && !isOffline && (
        <>
          <section className="pt-4 border-t border-slate-200">
            <h3 className="mb-2 text-sm font-semibold text-slate-900">Queue Firmware Update</h3>
            <DeviceTypeFirmware siteId={lock.site?.id} modelType={lock.deviceType} />
            {gatewayInfo?.information?.deviceTypeFirmware?.map((dtf) => (
              <DeviceTypeFirmware
                key={dtf.modelType}
                siteId={lock.site?.id}
                modelType={dtf.modelType}
              />
            ))}
          </section>

          {gatewayInfo?.locks && gatewayInfo.locks.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-900">Device Firmware Status</h3>
              <AdminTable aria-label="Gateway device firmware status">
                <thead>
                  <tr>
                    <th scope="col">Link</th>
                    <th scope="col">Name</th>
                    <th scope="col">S/N</th>
                    <th scope="col">Retries</th>
                    <th scope="col">Firmware</th>
                    <th scope="col">Transfer</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {gatewayInfo.locks.map((lk) => {
                    const clf = gatewayInfo?.information?.deviceTypeFirmware
                      ?.find((dtf) => dtf.modelType === lk.modelType)
                      ?.connectedLockFirmware?.find((c) => c.deviceId === lk.deviceId);
                    return (
                      <tr key={lk.deviceId}>
                        <td className="font-mono text-xs">{lk.linkId}</td>
                        <td>{lk.deviceName}</td>
                        <td className="font-mono text-xs">{lk.deviceId}</td>
                        <td>{clf?.retries ?? "—"}</td>
                        <td>{clf?.firmwareVersion ?? "—"}</td>
                        <td>{clf?.transferPercentage ?? "—"}</td>
                        <td>{clf?.updateStatus ?? "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </AdminTable>
            </section>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Time tab
// ─────────────────────────────────────────────────────────────────────────────

function TimeTab({
  lockId,
  isOffline,
  siteTimeZone,
}: {
  lockId: string;
  isOffline: boolean;
  siteTimeZone?: string;
}) {
  const queryClient = useQueryClient();
  const [showForce, setShowForce] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["locks", "gateway", "time-drift", lockId],
    queryFn: () => fetchGatewayTimeDrift(lockId),
    enabled: !isOffline,
  });

  const mutation = useMutation({
    mutationFn: (force: boolean) =>
      postGatewayTimeSync(lockId, { force }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["locks", "gateway", "time-drift", lockId],
      });
      setShowForce(false);
    },
  });

  const handleSync = async () => {
    try {
      await mutation.mutateAsync(false);
      setShowForce(false);
    } catch (err: any) {
      const errorCode = err?.response?.status;
      const errorMessage = err?.response?.data?.code || err?.response?.data?.message || "";
      // If drift is not severe enough (typically 400), show force option
      if (
        errorCode === 400 ||
        errorMessage.toLowerCase().includes("insufficient") ||
        errorMessage.toLowerCase().includes("not severe")
      ) {
        setShowForce(true);
      }
    }
  };

  const handleForceSync = () => {
    mutation.mutate(true);
  };

  if (isOffline) {
    return <p className="text-sm text-slate-500">Time sync unavailable — gateway offline.</p>;
  }

  const effectiveSiteTimeZone = siteTimeZone || data?.siteTimeZone || "UTC";

  const getServerDisplay = () => {
    if (data?.serverSiteLocalFormatted) {
      return data.serverSiteLocalFormatted;
    }

    if (data?.serverUtc) {
      return new Date(data.serverUtc).toISOString().replace("T", " ").replace("Z", " UTC");
    }

    return "—";
  };

  const getGatewayDisplay = () => {
    if (data?.gatewaySiteLocalFormatted) {
      return data.gatewaySiteLocalFormatted;
    }

    if (data?.gatewayUtc) {
      return new Date(data.gatewayUtc).toISOString().replace("T", " ").replace("Z", " UTC");
    }

    return "—";
  };

  const formatDrift = (seconds?: number) => {
    if (seconds == null) {
      return "—";
    }

    const totalSeconds = Math.abs(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes.toString().padStart(2, "0")}m ${remainingSeconds
        .toString()
        .padStart(2, "0")}s`;
    }

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds.toString().padStart(2, "0")}s`;
    }

    return `${remainingSeconds}s`;
  };

  return (
    <div className="space-y-4">
      {isLoading && <div className="text-sm text-slate-500">Loading drift data...</div>}
      {isError && (
        <Alert variant="destructive">Unable to load gateway time drift.</Alert>
      )}
      {data && (
        <div className="flex flex-wrap items-end p-4 bg-white border rounded-lg shadow-sm gap-x-8 gap-y-4 border-slate-200">
          <div className="min-w-55">
            <div className="mb-1 text-xs font-semibold uppercase text-slate-500">
              Site Time Zone
            </div>
            <div className="font-mono text-sm text-slate-800">{effectiveSiteTimeZone}</div>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase text-slate-500">
              Drift State
            </div>
            <div
              className={`font-semibold capitalize ${DRIFT_STATE_CLASS_NAMES[data.driftState]}`}
            >
              {data.driftState}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase text-slate-500">
              Drift
            </div>
            <div className="text-slate-800">{formatDrift(data.driftSeconds)}</div>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase text-slate-500">
              Gateway Time
            </div>
            <div className="font-mono text-sm text-slate-800">
              {getGatewayDisplay()}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase text-slate-500">
              Server Time
            </div>
            <div className="font-mono text-sm text-slate-800">
              {getServerDisplay()}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase text-slate-500">
              DST
            </div>
            <div className="text-slate-800">
              {data.dstSupported
                ? data.dstActive
                  ? "Active"
                  : "Inactive"
                : "Not supported"}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              disabled={mutation.isPending}
              onClick={handleSync}
              size="sm"
              type="button"
            >
              {mutation.isPending
                ? "Syncing..."
                : showForce
                  ? "Insufficient drift — Force?"
                  : "Sync Time"}
            </Button>
            {showForce && (
              <Button
                disabled={mutation.isPending}
                onClick={handleForceSync}
                size="sm"
                type="button"
                variant="warning"
              >
                {mutation.isPending ? "Forcing..." : "Force Sync"}
              </Button>
            )}
            <Button
              disabled={isLoading}
              onClick={() =>
                refetch()
              }
              size="sm"
              type="button"
              variant="outline"
            >
              Refresh
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// API helpers
// ─────────────────────────────────────────────────────────────────────────────

async function fetchGatewayInfo(lockId: string): Promise<GatewayInfoResponse> {
  const { data } = await axios.get<GatewayInfoResponse>(
    `${BASE_API_URL}/api/Locks/${lockId}/gateway`,
  );
  return data;
}

async function fetchGatewayTimeDrift(lockId: string): Promise<GatewayTimeDriftResponse> {
  const { data } = await axios.get<GatewayTimeDriftResponse>(
    `${BASE_API_URL}/api/locks/${lockId}/gateway/time-drift`,
  );
  return data;
}

async function postGatewayTimeSync(
  lockId: string,
  request: { force: boolean },
): Promise<GatewayTimeDriftResponse> {
  const { data } = await axios.post<GatewayTimeDriftResponse>(
    `${BASE_API_URL}/api/locks/${lockId}/gateway/time-sync`,
    request,
  );
  return data;
}
